import React, { useMemo } from 'react'

import { useFloorState } from '../../../firebase/hooks/useFloor'
import { useEditingMixState, useMixes } from '../../../firebase/hooks/useMixes'
import type { Mix } from '../../../firebase/model/mix'
import type { DeepReadonly } from '../../../firebase/typeUtils'

import { GraphicElement } from './GraphicElement/GraphicElement'
import { TextElement } from './TextElement/TextElement'

const findUniqueKey = (set: Set<string>, key: string | number) => {
  let uniqueKey = String(key)
  while (set.has(uniqueKey)) {
    uniqueKey += '+'
  }
  set.add(uniqueKey)
  return uniqueKey
}

const StaticElements = () => {
  const mixes = useMixes()
  const floor = useFloorState()
  const editingMix = useEditingMixState()

  const mix = useMemo(() => {
    if (editingMix) return editingMix

    if (floor?.layout === 'custom')
      return mixes.find((m) => m.id === floor.mixId)

    return null
  }, [editingMix, floor, mixes])

  if (!mix) return null

  return <StaticElementsForMix mix={mix} />
}

interface StaticElementsForMixProps {
  mix: DeepReadonly<Mix>
}

const StaticElementsForMix = ({ mix }: StaticElementsForMixProps) => {
  const keySet = new Set<string>()

  return (
    <>
      {mix.elements.map((el) => {
        switch (el.type) {
          case 'graphic':
            return (
              <GraphicElement
                element={el}
                key={findUniqueKey(
                  keySet,
                  el.style?.backgroundImage || el.index
                )}
              />
            )
          case 'text':
            return (
              <TextElement
                element={el}
                key={findUniqueKey(keySet, el.content || el.index)}
              />
            )
          default:
            return null
        }
      })}
    </>
  )
}

export { StaticElements, StaticElementsForMix }
